import React, { useEffect, useState, type CSSProperties } from "react";

import _, { isNaN } from "lodash";
import { twMerge } from "tailwind-merge";

import { GenericExternalLink } from "@components/generic-external-link";
import { useNorthbeamQuery } from "@hooks/http";
import {
  ConnectionType,
  type FetchSanitizedConnectionsByType,
  type FetchSanitizedConnectionsByTypeVariables,
  type FetchSanitizedConnectionsByType_me_sanitizedConnections,
} from "@north-beam/nb-client-side";
import {
  jsonHash,
  type AccountValidationInfo,
  type ConnectionIssue,
  type ConnectionValidationResult,
} from "@north-beam/nb-common";

import { AddAdditionalPrimaryAccount } from "./add-additional-primary-account";
import AddAdditionalSubAccount from "./add-additional-sub-account";
import ConnectionStatusManager, {
  type LoginComponentChildrenProps,
} from "./connection-status-manager";
import DeleteConnectionButton from "./delete-connection-button";
import ManageExistingConnection from "./manage-existing-connection";
import { FETCH_SANITIZED_CONNECTIONS_BY_TYPE } from "./queries";
import ShopifyStatusLine from "./shopify-status-line";
import UpdateLoginCustomerId from "./update-login-customer-id";

type Connection = FetchSanitizedConnectionsByType_me_sanitizedConnections;

enum ConnectionStatusType {
  INITIAL_LOADING = "InitialLoading",
  SUCCESSFULLY_CONNECTED = "SuccessfullyConnected",
  REQUIRES_CONNECTION = "RequiresConnection",
  HAS_ERROR = "HasError",
}
interface InitialLoading {
  type: ConnectionStatusType.INITIAL_LOADING;
  connection?: null;
  connections?: null;
  validationResults?: null;
}

interface SuccessfullyConnected {
  type: ConnectionStatusType.SUCCESSFULLY_CONNECTED;
  connection: Connection;
  connections: Connection[];
  validationResults: ConnectionValidationResult[];
}

interface RequiresConnection {
  type: ConnectionStatusType.REQUIRES_CONNECTION;
  connection: Connection | null;
  connections: Connection[];
  validationResults: ConnectionValidationResult[];
}
interface HasError {
  type: ConnectionStatusType.HAS_ERROR;
  errorMessage: string;
  connection?: null;
  connections?: null;
  validationResults?: ConnectionValidationResult[];
}

type Status =
  | InitialLoading
  | SuccessfullyConnected
  | RequiresConnection
  | HasError;

const divCardStyle: CSSProperties = {
  minHeight: "5em",
};

export interface ConnectionComponentProps {
  id: string | null;
  integrationDetails: any;
}

export interface PartnerConsentComponentProps {
  hideWhenMissingConsent?: boolean;
}

export type MultipleAccountsType = "primaryAccounts" | "subAccounts";

export interface ConnectionCardProps {
  type: ConnectionType;
  connectionTypeName: string;
  connectionCallToAction: string;
  precontent?: React.ReactNode;
  logoUrl: string;
  logoAltText: string;
  children: (props: LoginComponentChildrenProps) => React.ReactNode;
  successfulConnectionExtraText?: () => React.ReactNode;
  supportMultipleAccounts?: MultipleAccountsType;
  cardFooter?: React.ReactNode;
  knowledgeBaseLink?: string;
  partnerConsentComponent?: (
    props: PartnerConsentComponentProps,
  ) => React.ReactNode;
}

export function ConnectionCard({
  type,
  connectionTypeName,
  connectionCallToAction,
  precontent,
  logoUrl,
  logoAltText,
  children,
  successfulConnectionExtraText,
  supportMultipleAccounts,
  cardFooter,
  knowledgeBaseLink,
  partnerConsentComponent,
}: ConnectionCardProps) {
  const [allProfiles, setAllProfiles] = useState<any>([]);
  const { data, loading: fetchLoading } = useNorthbeamQuery<
    FetchSanitizedConnectionsByType,
    FetchSanitizedConnectionsByTypeVariables
  >(FETCH_SANITIZED_CONNECTIONS_BY_TYPE, {
    variables: { type },
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const tempAllProfiles: any[] = [];
    data?.me?.sanitizedConnections.forEach((connection) => {
      tempAllProfiles.push(connection.allProfiles);
    });
    setAllProfiles(tempAllProfiles);
  }, [data]);

  const [status, setStatus] = useState<Status>({
    type: ConnectionStatusType.INITIAL_LOADING,
  });

  useEffect(() => {
    if (fetchLoading) {
      setStatus({
        type: ConnectionStatusType.INITIAL_LOADING,
      });
    } else {
      /* TODO - refactor the singular connection, it is needed to handle multiple accounts in
         metadata handle this by making each of those account into its own connection in
         connections, then remove the logic to display multiple accounts in metadata */
      const connection = data?.me.sanitizedConnections[0] ?? null;
      const connections: Connection[] = data?.me.sanitizedConnections || [];
      const validationResults: ConnectionValidationResult[] = [];
      if (connections.length) {
        connections.forEach((connection) => {
          const connectionValidation =
            connection.connectionValidation as ConnectionValidationResult;
          validationResults.push(connectionValidation);
        });
      }

      const connectedAccounts: AccountValidationInfo[] = [];
      validationResults.forEach((result) => {
        if (result.connectedAccounts.length) {
          connectedAccounts.push(...result.connectedAccounts);
        }
      });

      const allConnectionLevelIssues: ConnectionIssue[] = [];
      validationResults.forEach((result) => {
        allConnectionLevelIssues.push(...result.connectionLevelIssues);
      });
      if (
        !connection ||
        !validationResults.length ||
        allConnectionLevelIssues.some((v) => v.reconnectRecommended) ||
        connectedAccounts.some((v) => v.connectionIssue?.reconnectRecommended)
      ) {
        setStatus({
          type: ConnectionStatusType.REQUIRES_CONNECTION,
          connection,
          connections,
          validationResults,
        });
      } else {
        setStatus({
          type: ConnectionStatusType.SUCCESSFULLY_CONNECTED,
          connection: connection as Connection,
          connections,
          validationResults,
        });
      }
    }
  }, [data, fetchLoading]);

  const connectedAccounts: AccountValidationInfo[] = [];
  if (status.validationResults) {
    /* amazon seller has multiple validation results and each result has multiple connected accounts
       get all connected accounts for each validation result */
    status.validationResults.forEach((result) => {
      if (result?.connectedAccounts.length) {
        connectedAccounts.push(...result.connectedAccounts);
      }
    });
  }

  const shouldAllowMultipleConnections =
    supportMultipleAccounts &&
    status.type === ConnectionStatusType.SUCCESSFULLY_CONNECTED &&
    status?.connection?.id;

  const renderDeleteConnectionButton = (
    accountId: string,
    connection?: Connection | null,
  ) => {
    if (!connection?.id) {
      return null;
    }

    const additionalAccounts =
      status?.connection?.data.metadata?.extraAccountIds || [];

    return (
      <ManageExistingConnection
        connectionId={connection.id}
        connectionType={type}
      >
        {(updateConnection, updateInfo, destroyConnection, destroyInfo) => (
          <DeleteConnectionButton
            updateConnection={updateConnection}
            updateInfo={updateInfo}
            destroyConnection={destroyConnection}
            destroyInfo={destroyInfo}
            additionalAccounts={additionalAccounts}
            accountId={accountId}
            connectionType={type}
          />
        )}
      </ManageExistingConnection>
    );
  };

  function renderAccountValidationInfoAsStatusLine(
    account: AccountValidationInfo,
    key: number | string,
  ) {
    if (status.connection?.type === ConnectionType.integration_shopify) {
      return (
        <ManageExistingConnection
          key={type}
          connectionId={status.connection.id}
          connectionType={type}
        >
          {(updateConnection, updateInfo, destroyConnection, destroyInfo) => (
            <ShopifyStatusLine
              key="shopify-status-line"
              connectionIssue={account.connectionIssue}
              shopUrl={account.name}
              destroyConnection={destroyConnection}
              destroyInfo={destroyInfo}
            />
          )}
        </ManageExistingConnection>
      );
    }
    const emoji = account.connectionIssue ? "🚨" : "✔️";
    const suffix = account.connectionIssue ? (
      <tr>
        <td colSpan={4}>
          <div
            className="mt-1 alert alert-warning"
            style={{ whiteSpace: "pre-line" }}
          >
            <b>Error</b>: <em>{account.connectionIssue.message}</em>
          </div>
        </td>
      </tr>
    ) : null;
    const id =
      typeof account.accountIdentifier === "string"
        ? account.accountIdentifier
        : jsonHash(account.accountIdentifier);

    const baseConnection =
      supportMultipleAccounts === "primaryAccounts"
        ? status.connections?.find((connection) => {
            const connectedAccountsIdentifiers =
              connection.connectionValidation.connectedAccounts.map(
                (connectedAccount: AccountValidationInfo) =>
                  connectedAccount.accountIdentifier,
              );
            return connectedAccountsIdentifiers.includes(
              account.accountIdentifier,
            );
          })
        : status.connection;

    let amazonSellerIdString = "";
    if (status.connection?.type === ConnectionType.integration_amazon_seller) {
      const countryCountMap: any = _.countBy(id.split(", "));
      Object.keys(countryCountMap).forEach((country) => {
        amazonSellerIdString += `${country}(${countryCountMap[country]}) `;
      });
    }

    const getDetailsString = (
      connection?: FetchSanitizedConnectionsByType_me_sanitizedConnections | null,
    ) => {
      if (connection?.type === ConnectionType.integration_amazon_seller) {
        return amazonSellerIdString;
      } else if (connection?.type === ConnectionType.integration_applovin) {
        return "API Reporting Key: XXXX";
      } else if (connection?.type === ConnectionType.integration_mntn) {
        return "MNTN Account ID: XXXXX";
      } else if (connection?.type === ConnectionType.integration_tatari) {
        return "Tatari ID: XXXX";
      }
      return id;
    };
    return (
      <React.Fragment key={id ?? key}>
        <tr className="tr text-small">
          <td className="td">{emoji}</td>
          <td className="td">{account.name}</td>
          <td className="text-xs break-words td">
            {getDetailsString(status.connection)}
          </td>
          <td className="td">
            {renderDeleteConnectionButton(id, baseConnection)}
          </td>
        </tr>
        {suffix}
      </React.Fragment>
    );
  }

  function renderConnectionLevelIssueAsStatusLine(
    connectionIssue: ConnectionIssue,
  ) {
    const emoji = "🚨";
    return (
      <li key={jsonHash(connectionIssue)}>
        {emoji} [Connection-level] <b>Error</b>:{" "}
        <em>{connectionIssue.message}</em>
      </li>
    );
  }

  const shouldAllowLoginCustomerAccountChange = () =>
    type === ConnectionType.integration_google_adwords;

  const renderManagementSection = () => {
    if (
      status.type === ConnectionStatusType.INITIAL_LOADING ||
      status?.connection === null
    ) {
      return null;
    }

    if (
      shouldAllowMultipleConnections &&
      supportMultipleAccounts === "subAccounts"
    ) {
      return (
        <ManageExistingConnection
          connectionId={status.connection.id}
          connectionType={type}
        >
          {(updateConnection) => (
            <>
              {shouldAllowLoginCustomerAccountChange() && (
                <UpdateLoginCustomerId
                  loginCustomerId={status?.connection?.data.login_customer_id}
                  updateConnection={updateConnection}
                  parseInput={
                    // eslint-disable-next-line max-len
                    /* right now we only have this for adwords, so specifing the validation function here
                       https://support.google.com/google-ads/thread/2355961/how-to-find-your-customer-id?hl=en#:~:text=A%20Customer%20ID%20is%20a,MCC's%20(My%20Client%20Centre). */
                    (input) => {
                      const parsedInput = input.trim().replace(/[ \- _]/g, "");

                      if (parsedInput.length !== 10) {
                        return {
                          type: "error",
                          msg: "An Adwords Customer Id must have 10 digits",
                        };
                      }
                      if (isNaN(Number(parsedInput))) {
                        return {
                          type: "error",
                          msg: "An Adwords Customer Id must only have digits",
                        };
                      }
                      return {
                        type: "parsed",
                        parsedInput,
                      };
                    }
                  }
                />
              )}
              <AddAdditionalSubAccount
                integrationType={status?.connection?.type}
                connectedAccounts={connectedAccounts}
                extraAccountIds={
                  status?.connection?.data.metadata.extraAccountIds
                }
                mainLoginCustomerId={status?.connection?.data.login_customer_id}
                updateConnection={updateConnection}
                availableAccounts={allProfiles}
              />
            </>
          )}
        </ManageExistingConnection>
      );
    } else if (
      shouldAllowMultipleConnections &&
      supportMultipleAccounts === "primaryAccounts"
    ) {
      return (
        <AddAdditionalPrimaryAccount
          connectionType={type}
          connectedAccounts={connectedAccounts}
        >
          {children}
        </AddAdditionalPrimaryAccount>
      );
    } else {
      return null;
    }
  };

  const renderSuccessfulConnection = () => {
    if (
      status.type !== ConnectionStatusType.SUCCESSFULLY_CONNECTED ||
      shouldAllowMultipleConnections
    ) {
      return null;
    }
    if (successfulConnectionExtraText) {
      return successfulConnectionExtraText();
    }
    return (
      <p className="card-text">
        <em>
          Note: if you want to connect a different account, please contact us!
        </em>
      </p>
    );
  };

  const partnerConsent = partnerConsentComponent?.({
    hideWhenMissingConsent:
      status.type !== ConnectionStatusType.SUCCESSFULLY_CONNECTED,
  });

  let content: React.ReactNode;

  const shouldRenderShopifyExtraAccounts =
    status.connection?.type === ConnectionType.integration_shopify &&
    connectedAccounts.length;

  if (status.type === ConnectionStatusType.INITIAL_LOADING) {
    content = (
      <div style={divCardStyle} className="flex items-center text-center">
        <p>Please wait...</p>
      </div>
    );
  } else if (
    status.type === ConnectionStatusType.SUCCESSFULLY_CONNECTED ||
    shouldRenderShopifyExtraAccounts
  ) {
    content = (
      <>
        {status.connection?.data?.ownerName && (
          <div>
            <span className="font-semibold">Connected Account:</span>{" "}
            <span>
              {status.connection?.data?.ownerName}
              {status.connection?.data?.ownerEmail
                ? ` (${status.connection?.data?.ownerEmail})`
                : ""}
            </span>
          </div>
        )}
        <p className="card-text">Currently connected! Linked accounts:</p>
        {partnerConsent}
        <table className="table" style={{ tableLayout: "fixed" }}>
          <thead>
            <tr className="tr">
              <th className="w-1/12 th" />
              <th className="th">Name</th>
              <th className="th">Details</th>
              <th className="th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {connectedAccounts.map(renderAccountValidationInfoAsStatusLine)}
          </tbody>
        </table>
        {status.connection?.type ===
        ConnectionType.integration_amazon_seller ? (
          <ConnectionStatusManager
            id={null}
            type={type}
            accounts={connectedAccounts}
          >
            {children}
          </ConnectionStatusManager>
        ) : (
          renderManagementSection()
        )}
        {status.connection?.type !== ConnectionType.integration_amazon_seller &&
          renderSuccessfulConnection()}
      </>
    );
  } else {
    let header = null;
    if (status.validationResults?.length) {
      const allConnectionLevelIssues: ConnectionIssue[] = [];
      status.validationResults.forEach((result) => {
        allConnectionLevelIssues.push(...result.connectionLevelIssues);
      });
      const statusLines = [
        ...connectedAccounts
          .filter((v) => v.connectionIssue?.reconnectRecommended)
          .map(renderAccountValidationInfoAsStatusLine),
        ...allConnectionLevelIssues.map(renderConnectionLevelIssueAsStatusLine),
      ];
      header = (
        <div className="alert alert-danger" role="alert">
          <p>We ran into a few issues! Please reconnect your account below.</p>
          <ul className="mb-0">{statusLines}</ul>
        </div>
      );
    }
    content = (
      <>
        {partnerConsent}
        {header}
        {supportMultipleAccounts === "primaryAccounts" &&
          !!connectedAccounts.length && (
            <table className="table" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr className="tr">
                  <th className="w-1/12 th" />
                  <th className="th">Name</th>
                  <th className="th">Details</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {connectedAccounts.map(renderAccountValidationInfoAsStatusLine)}
              </tbody>
            </table>
          )}
        <p className="card-text">{connectionCallToAction}</p>
        <ConnectionStatusManager
          id={status.connection?.id ?? null}
          type={type}
          accounts={connectedAccounts}
        >
          {children}
        </ConnectionStatusManager>
        {renderManagementSection()}
      </>
    );
  }

  return (
    <div className="mb-3 rounded-md card">
      <div className="flex card-header">
        <div className="flex-fill">
          <img
            src={logoUrl}
            alt={logoAltText}
            className="mr-3 align-self-start"
            style={{
              height: "2rem",
              width: "auto",
            }}
          />
          {connectionTypeName}
        </div>
      </div>
      <div className="card-body">
        {knowledgeBaseLink && (
          <KnowledgeBaseLearnMoreLink
            className="mb-4"
            connectionTypeName={connectionTypeName}
            knowledgeBaseLink={knowledgeBaseLink}
          />
        )}
        {precontent}
        {content}
        {cardFooter}
      </div>
    </div>
  );
}

interface KnowledgeBaseLearnMoreLinkProps
  extends Required<
    Pick<ConnectionCardProps, "connectionTypeName" | "knowledgeBaseLink">
  > {
  className?: string;
}

export const KnowledgeBaseLearnMoreLink = ({
  connectionTypeName,
  knowledgeBaseLink,
  className,
}: KnowledgeBaseLearnMoreLinkProps) => {
  return (
    <p className={twMerge("card-text", className)}>
      <em>
        Learn more about our {connectionTypeName} Integration{" "}
        <GenericExternalLink href={knowledgeBaseLink}>
          here.
        </GenericExternalLink>
      </em>
    </p>
  );
};
